"use client";

/**
 * Phase 9 (origin) / Phase 11 Plan 11-05 (retired bundle path) — Pricing-page
 * "We Set It Up For You" service card.
 *
 * Mounted below the existing 3-tier <PricingCards> grid in
 * app/(marketing)/pricing/page.tsx. Wrapped in
 * useAppFeatureFlag('setup_service') — when off, renders nothing
 * (no skeleton, no reflow placeholder; UI-SPEC §1).
 *
 * Visual contract: UI-SPEC §1 — accented card mirroring the recommended-tier
 * Pro plan treatment. Heading + price + CTA must fit above the fold on a
 * 375px viewport.
 *
 * Phase 11 Plan 11-05 retired the free-tier `CheckoutSummary` bundle path.
 * The CTA now uniformly fires the server-action redirect to Stripe Checkout
 * for logged-out + free buyers; the existing-Pro D-06 path is preserved
 * inside the CTA button itself.
 *
 * Auth state is resolved server-side and passed as a prop (Pitfall 5 /
 * T-AUTH-RACE-MARKETING).
 *
 * Analytics:
 * - `setup_service_viewed` fires once on first ≥50% IntersectionObserver
 *   intersection (useRef guard prevents re-fires on scroll-back).
 * - `setup_service_cta_clicked` fires from <SetupServiceCtaButton>.
 */

import { useEffect, useRef } from "react";
import Link from "next/link";
import { CheckCircle2 } from "lucide-react";
import { useAppFeatureFlag } from "@/components/feature-flags/feature-flags-provider";

import {
  PRICING_CARD,
  SETUP_SERVICE_PRICE_DISPLAY,
  SETUP_SERVICE_PRICE_PERIOD,
  SETUP_SERVICE_TURNAROUND,
} from "@/lib/setup-service-copy";
import {
  trackSetupServiceEvent,
  deriveDeviceType,
} from "@/lib/setup-service-analytics";
import {
  SetupServiceCtaButton,
  type SetupServiceAuthState,
} from "@/components/setup-service/setup-service-cta-button";

export interface SetupServicePricingCardProps {
  /** Server-resolved Clerk auth + plan + setup-service status. */
  authState: SetupServiceAuthState;
  /**
   * Phase 9 prop kept in the type for source-compat with callers. Phase 11
   * Plan 11-05 retired the bundle-checkout path so this is no longer read.
   * Will be removed in a future cleanup once external callers migrate.
   */
  defaultPriceId?: string;
}

export function SetupServicePricingCard(props: SetupServicePricingCardProps) {
  const { authState } = props;

  const flagEnabled = useAppFeatureFlag("setup_service");

  const cardRef = useRef<HTMLDivElement | null>(null);
  const viewedFiredRef = useRef(false);

  useEffect(() => {
    // Pitfall 2 — flag undefined or false means "off". Don't observe.
    if (flagEnabled !== true) return;
    const node = cardRef.current;
    if (!node || typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !viewedFiredRef.current) {
            viewedFiredRef.current = true;
            trackSetupServiceEvent("setup_service_viewed", {
              source_page: "pricing",
              device_type: deriveDeviceType(),
              plan_selected: null,
            });
            observer.disconnect();
          }
        }
      },
      // F-09-03 — include 0 so the callback fires on initial mount even when
      // the card is already 100% intersecting (e.g. tall desktop viewport).
      // Threshold-crossing semantics mean a single 0.5 threshold can miss
      // first paint; the >=0.5 guard inside the callback enforces visibility.
      { threshold: [0, 0.5] },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [flagEnabled]);

  // Pitfall 2 — render nothing while flag is undefined or false.
  if (flagEnabled !== true) return null;

  return (
    <div ref={cardRef} className="mx-auto mt-12 max-w-3xl text-left lg:mt-16">
      <div className="rounded-2xl border-2 border-accent bg-accent-light/30 p-8 shadow-lg ring-2 ring-accent/20">
        <h3 className="font-heading text-lg font-bold text-navy">{PRICING_CARD.heading}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted">{PRICING_CARD.body}</p>

        <p className="mt-4">
          <span className="text-4xl font-extrabold text-navy">{SETUP_SERVICE_PRICE_DISPLAY}</span>
          <span className="ml-2 text-sm text-muted">{SETUP_SERVICE_PRICE_PERIOD}</span>
        </p>

        <p className="mt-2">
          <span className="inline-block rounded-md bg-done-bg px-2 py-1 text-xs font-medium text-done">
            {SETUP_SERVICE_TURNAROUND}
          </span>
        </p>

        <ul className="mt-6 space-y-3">
          {PRICING_CARD.bullets.map((bullet) => (
            <li key={bullet} className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
              <span className="text-sm text-muted">{bullet}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8">
          <SetupServiceCtaButton
            location="pricing"
            authState={authState}
            planSelected={null}
            ctaLabel={PRICING_CARD.cta}
          />
        </div>

        {/* G3-8d — secondary "talk to us first" path to the lead form on
            /setup-for-you. The £249 self-serve CTA above stays primary. */}
        <p className="mt-4 text-sm text-muted">
          Prefer to talk it through first?{" "}
          <Link
            href="/setup-for-you#talk-to-us"
            onClick={() =>
              trackSetupServiceEvent("setup_service_cta_clicked", {
                source_page: "pricing",
                device_type: deriveDeviceType(),
                plan_selected: null,
                cta_label: "talk_to_us_link",
              })
            }
            className="font-semibold text-brand-700 underline hover:text-accent-dark"
          >
            Talk to us
          </Link>
        </p>
      </div>
    </div>
  );
}
